/**
 * Shared styled Excel export helper (ExcelJS — already a project dependency).
 * Produces: report title + filter/detail lines, bordered header row, bordered
 * data rows with proper alignment (numeric right-aligned + formatted, text
 * left-aligned), a bold total row for numeric columns, and auto-fit column
 * widths — instead of a bare, unstyled `json_to_sheet` dump.
 */

const THIN_BORDER = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };

/**
 * Renders the title/detail lines shown above the header row, shared by exportStyledExcel and
 * exportStyledExcelMultiSheet so both support the same line shapes. A filter line is one of:
 *  - a plain string (rendered in the default grey/10pt style)
 *  - { text, bold, size, color, center, fill } for a single full-width line that needs to
 *    stand out, e.g. the organization name
 *  - { left, right, bold, size, color, fill } for one line split left/right across the row
 *    (e.g. "Period: ..." on the left, "Branch: ..." on the right) — mirrors how the PDF
 *    export lays the same details out.
 */
const renderFilterLines = (worksheet, filterLines, colCount) => {
    filterLines.filter(Boolean).forEach((line) => {
        const isObj = typeof line === 'object';
        const isPaired = isObj && line.text === undefined && (line.left !== undefined || line.right !== undefined);
        const font = { bold: isObj ? !!line.bold : false, size: isObj && line.size ? line.size : 10, color: { argb: isObj && line.color ? line.color : 'FF666666' } };
        const fill = isObj && line.fill ? { type: 'pattern', pattern: 'solid', fgColor: { argb: line.fill } } : null;

        if (isPaired) {
            if (!line.left && !line.right) return;
            const row = worksheet.addRow([]);
            const mid = Math.max(1, Math.ceil(colCount / 2));
            if (line.left) {
                worksheet.mergeCells(row.number, 1, row.number, mid);
                const cell = row.getCell(1);
                cell.value = line.left;
                cell.font = font;
                cell.alignment = { horizontal: 'left', vertical: 'middle' };
                if (fill) cell.fill = fill;
            }
            if (line.right) {
                worksheet.mergeCells(row.number, mid + 1, row.number, colCount);
                const cell = row.getCell(mid + 1);
                cell.value = line.right;
                cell.font = font;
                cell.alignment = { horizontal: 'right', vertical: 'middle' };
                if (fill) cell.fill = fill;
            }
            if (fill) {
                for (let c = 1; c <= colCount; c++) row.getCell(c).fill = fill;
            }
            return;
        }

        const text = isObj ? line.text : line;
        if (!text) return;
        const row = worksheet.addRow([text]);
        worksheet.mergeCells(row.number, 1, row.number, colCount);
        row.font = font;
        row.alignment = { horizontal: isObj && line.center ? 'center' : 'left' };
        if (fill) row.eachCell({ includeEmpty: true }, (cell) => (cell.fill = fill));
    });
};

/**
 * @param {Object} opts
 * @param {string} opts.fileName - e.g. 'ItemFullReport_2026-07-13.xlsx'
 * @param {string} [opts.sheetName] - defaults to a trimmed version of the title
 * @param {string} opts.title - report title, shown merged across all columns
 * @param {string[]} [opts.filterLines] - e.g. ['Period: 01-07-2026 to 05-07-2026', 'Branch: All Branches']
 * @param {Array<{key:string, header:string, getValue:(row:any, index:number)=>any, numeric?:boolean, skipSum?:boolean}>} opts.columns
 * @param {any[]} opts.rows - raw row objects; `getValue` extracts each column's value per row
 * @param {string} [opts.footerLabel] - label shown in the first column of the total row (default 'Total')
 * @param {boolean} [opts.showFooterTotal] - set false to omit the total row entirely (e.g. role-gated totals) (default true)
 * @param {Array} [opts.footerExtraLines] - extra detail lines rendered after the total row (same shapes as filterLines, e.g. Starting/Ending Invoice No)
 */
export const exportStyledExcel = async ({ fileName, sheetName, title, filterLines = [], columns, rows = [], footerLabel = 'Total', showFooterTotal = true, footerExtraLines = [] }) => {
    const ExcelJSModule = await import('exceljs');
    const ExcelJS = ExcelJSModule.default || ExcelJSModule;
    const { saveAs } = await import('file-saver');

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet((sheetName || title || 'Report').slice(0, 31));
    const colCount = columns.length;

    if (title) {
        const titleRow = worksheet.addRow([title]);
        worksheet.mergeCells(titleRow.number, 1, titleRow.number, colCount);
        titleRow.font = { bold: true, size: 14 };
    }

    renderFilterLines(worksheet, filterLines, colCount);

    if (title || filterLines.length) worksheet.addRow([]);

    const headerRow = worksheet.addRow(columns.map((c) => c.header));
    headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    headerRow.eachCell((cell) => {
        cell.border = THIN_BORDER;
        cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4A3AA7' } };
    });

    const totals = new Array(colCount).fill(0);
    const hasTotal = new Array(colCount).fill(false);

    rows.forEach((row, rowIndex) => {
        const values = columns.map((col, idx) => {
            const raw = col.getValue(row, rowIndex);
            if (col.numeric) {
                const n = typeof raw === 'number' ? raw : parseFloat(raw || 0) || 0;
                if (!col.skipSum) {
                    totals[idx] += n;
                    hasTotal[idx] = true;
                }
                return n;
            }
            return raw ?? '-';
        });
        const r = worksheet.addRow(values);
        r.eachCell((cell, colNumber) => {
            cell.border = THIN_BORDER;
            const col = columns[colNumber - 1];
            if (col.numeric) {
                cell.numFmt = '#,##0.00';
                cell.alignment = { horizontal: 'right' };
            } else {
                cell.alignment = { horizontal: 'left', vertical: 'middle' };
            }
            // Optional per-cell font color, e.g. flagging a reconciliation mismatch red/green.
            const argb = col.getColor?.(row, rowIndex);
            if (argb) cell.font = { ...(cell.font || {}), bold: true, color: { argb } };
        });
    });

    if (showFooterTotal && rows.length && hasTotal.some(Boolean)) {
        const footerValues = columns.map((col, idx) => (idx === 0 ? footerLabel : hasTotal[idx] ? totals[idx] : ''));
        const footerRow = worksheet.addRow(footerValues);
        footerRow.font = { bold: true };
        footerRow.eachCell((cell, colNumber) => {
            const col = columns[colNumber - 1];
            cell.border = { top: { style: 'thin' }, bottom: { style: 'double' } };
            if (col.numeric) {
                cell.numFmt = '#,##0.00';
                cell.alignment = { horizontal: 'right' };
            }
        });
    }

    if (footerExtraLines.length) {
        worksheet.addRow([]);
        renderFilterLines(worksheet, footerExtraLines, colCount);
    }

    // Only measure from the header row down — the merged title/filter rows above it
    // span every column with the same long text, which would otherwise force every
    // column to that same (wrong) width.
    worksheet.columns.forEach((column, idx) => {
        let maxLength = columns[idx]?.header?.length || 10;
        for (let rowNum = headerRow.number; rowNum <= worksheet.rowCount; rowNum++) {
            const cell = worksheet.getRow(rowNum).getCell(idx + 1);
            const len = cell.value != null ? String(cell.value).length : 0;
            if (len > maxLength) maxLength = len;
        }
        column.width = Math.min(Math.max(maxLength + 2, 10), 40);
    });

    worksheet.views = [{ state: 'frozen', ySplit: headerRow.number }];

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, fileName);
};

/**
 * Export multiple sheets in one workbook (e.g. Top Selling / Bottom Selling).
 * Each entry in `sheets` takes the same shape as exportStyledExcel minus fileName.
 */
export const exportStyledExcelMultiSheet = async ({ fileName, sheets }) => {
    const ExcelJSModule = await import('exceljs');
    const ExcelJS = ExcelJSModule.default || ExcelJSModule;
    const { saveAs } = await import('file-saver');

    const workbook = new ExcelJS.Workbook();

    sheets.forEach(({ sheetName, title, filterLines = [], columns, rows = [], footerLabel = 'Total' }) => {
        const worksheet = workbook.addWorksheet((sheetName || title || 'Sheet').slice(0, 31));
        const colCount = columns.length;

        if (title) {
            const titleRow = worksheet.addRow([title]);
            worksheet.mergeCells(titleRow.number, 1, titleRow.number, colCount);
            titleRow.font = { bold: true, size: 14 };
        }

        renderFilterLines(worksheet, filterLines, colCount);

        if (title || filterLines.length) worksheet.addRow([]);

        const headerRow = worksheet.addRow(columns.map((c) => c.header));
        headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
        headerRow.eachCell((cell) => {
            cell.border = THIN_BORDER;
            cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4A3AA7' } };
        });

        const totals = new Array(colCount).fill(0);
        const hasTotal = new Array(colCount).fill(false);

        rows.forEach((row) => {
            const values = columns.map((col, idx) => {
                const raw = col.getValue(row);
                if (col.numeric) {
                    const n = typeof raw === 'number' ? raw : parseFloat(raw || 0) || 0;
                    if (!col.skipSum) {
                        totals[idx] += n;
                        hasTotal[idx] = true;
                    }
                    return n;
                }
                return raw ?? '-';
            });
            const r = worksheet.addRow(values);
            r.eachCell((cell, colNumber) => {
                cell.border = THIN_BORDER;
                const col = columns[colNumber - 1];
                if (col.numeric) {
                    cell.numFmt = '#,##0.00';
                    cell.alignment = { horizontal: 'right' };
                } else {
                    cell.alignment = { horizontal: 'left', vertical: 'middle' };
                }
            });
        });

        if (rows.length && hasTotal.some(Boolean)) {
            const footerValues = columns.map((col, idx) => (idx === 0 ? footerLabel : hasTotal[idx] ? totals[idx] : ''));
            const footerRow = worksheet.addRow(footerValues);
            footerRow.font = { bold: true };
            footerRow.eachCell((cell, colNumber) => {
                const col = columns[colNumber - 1];
                cell.border = { top: { style: 'thin' }, bottom: { style: 'double' } };
                if (col.numeric) {
                    cell.numFmt = '#,##0.00';
                    cell.alignment = { horizontal: 'right' };
                }
            });
        }

        worksheet.columns.forEach((column, idx) => {
            let maxLength = columns[idx]?.header?.length || 10;
            for (let rowNum = headerRow.number; rowNum <= worksheet.rowCount; rowNum++) {
                const cell = worksheet.getRow(rowNum).getCell(idx + 1);
                const len = cell.value != null ? String(cell.value).length : 0;
                if (len > maxLength) maxLength = len;
            }
            column.width = Math.min(Math.max(maxLength + 2, 10), 40);
        });

        worksheet.views = [{ state: 'frozen', ySplit: headerRow.number }];
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, fileName);
};

/**
 * Build the standard filter/detail lines shown under the title in report exports:
 * organization name (bold, standalone), Period, Branch, invoice number range, Generated On.
 * @param {Object} opts
 * @param {string} [opts.organizationName] - from branch_creation.org_name; omitted if blank
 * @param {{start:string, end:string}} [opts.invoiceRange] - first/last invoice_no in the exported rows
 */
export const buildReportFilterLines = ({ fromDate, toDate, selectedBranch, organizationName, invoiceRange }) => {
    const lines = [];
    if (organizationName) {
        lines.push({ text: organizationName, bold: true, size: 12, center: true });
    }
    if (fromDate && toDate) {
        const fmt = (d) => (typeof d === 'object' && d?.toLocaleDateString ? d.toLocaleDateString('en-IN') : String(d));
        lines.push(`Period: ${fmt(fromDate)} to ${fmt(toDate)}`);
    }
    const branchName = selectedBranch && (selectedBranch.branch_name || selectedBranch);
    lines.push(`Branch: ${branchName || 'All Branches'}`);
    if (invoiceRange && (invoiceRange.start || invoiceRange.end)) {
        lines.push(`Invoice No: ${invoiceRange.start || '-'} to ${invoiceRange.end || '-'}`);
    }
    lines.push(`Generated On: ${new Date().toLocaleString('en-IN')}`);
    return lines;
};
